import React, { useState } from "react"

import { usePlugins } from "../../../recoil/pluginStudio/hooks"
import { UserRole } from "../../../recoil/pluginStudio/types"
import MetadataEditor from "./MetadataEditor"

interface HeaderPanelUIProps {
  title?: string
  pluginName?: string
  isEditMode?: boolean
  onEditMetadata?: () => void
  activeView?: UserRole
  onTabChange?: (view: UserRole) => void
  isLoading?: boolean
  children?: React.ReactNode
}

/**
 * Presentation component for the header panel
 */
export const HeaderPanelUI: React.FC<HeaderPanelUIProps> = ({
  title = "Plugin Studio",
  pluginName,
  isEditMode = false,
  onEditMetadata,
  children,
  activeView = UserRole.GUEST,
  onTabChange,
  isLoading,
}) => {
  return (
    <header className="bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
      <div className="flex items-center space-x-2">
        <h1 className="text-xl font-bold text-white">{title}</h1>
        {pluginName && (
          <div className="flex items-center">
            <span className="mx-2 text-gray-400">•</span>
            <span
              className={`text-gray-200 font-medium ${onEditMetadata ? "cursor-pointer hover:text-white hover:underline" : ""}`}
              onClick={onEditMetadata}
              title={onEditMetadata ? "Click to edit plugin details" : ""}
            >
              {pluginName}
            </span>
            {isEditMode && (
              <span className="ml-2 px-2 py-0.5 bg-blue-600 rounded-full text-xs text-white">
                Editing
              </span>
            )}
            {onEditMetadata && (
              <button
                onClick={onEditMetadata}
                className="ml-2 p-1 text-gray-400 hover:text-white rounded-full hover:bg-gray-700"
                title="Edit plugin details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
            )}
          </div>
        )}
        {children}
      </div>

      <div className="flex items-center space-x-4">
        {/* View toggle buttons */}
        {onTabChange && (
          <div className="flex mr-6 bg-gray-700 rounded overflow-hidden">
            <button
              className={`px-3 py-1 text-sm ${
                activeView === UserRole.GUEST
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-600"
              }`}
              onClick={() => onTabChange(UserRole.GUEST)}
            >
              Guest
            </button>
            <button
              className={`px-3 py-1 text-sm ${
                activeView === UserRole.HOST_EVENT_VIEW
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-600"
              }`}
              onClick={() => onTabChange(UserRole.HOST_EVENT_VIEW)}
            >
              Host Event
            </button>
            <button
              className={`px-3 py-1 text-sm ${
                activeView === UserRole.HOST_CONFIG_VIEW
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-gray-600"
              }`}
              onClick={() => onTabChange(UserRole.HOST_CONFIG_VIEW)}
            >
              Host Config
            </button>
          </div>
        )}

        {/* Navigation links */}
        <a href="/" className="text-gray-400 hover:text-white">
          Home
        </a>
        <a href="/explore" className="text-gray-400 hover:text-white">
          Explore
        </a>
        <a
          href="/plugin/studio/creation"
          className="text-gray-400 hover:text-white"
        >
          New Plugin
        </a>
        <a href="/search" className="text-gray-400 hover:text-white">
          Search
        </a>
      </div>
    </header>
  )
}

/**
 * Container component that connects to Recoil state
 */
const HeaderPanel: React.FC = () => {
  const { activePlugin, updatePlugin, setActiveViewType, activeFile } =
    usePlugins()
  const [isEditingMetadata, setIsEditingMetadata] = useState(false)

  const handleEditMetadata = () => {
    setIsEditingMetadata(true)
  }

  const handleSaveMetadata = (updatedMetadata: any) => {
    if (activePlugin) {
      updatePlugin(activePlugin.id, {
        manifest: {
          ...activePlugin.manifest,
          ...updatedMetadata,
        },
      })
    }
    setIsEditingMetadata(false)
  }

  const handleTabChange = (tab: UserRole) => {
    if (activePlugin) {
      setActiveViewType(activePlugin.id, tab)
    }
  }

  // If metadata editor is open, render that instead
  if (isEditingMetadata && activePlugin) {
    return (
      <MetadataEditor
        manifest={activePlugin.manifest}
        onSave={handleSaveMetadata}
        onCancel={() => setIsEditingMetadata(false)}
        open={isEditingMetadata}
      />
    )
  }

  // Determine the active view from the active file
  const activeView =
    activeFile &&
    (activeFile.type === UserRole.GUEST ||
      activeFile.type === UserRole.HOST_EVENT_VIEW ||
      activeFile.type === UserRole.HOST_CONFIG_VIEW)
      ? activeFile.type
      : UserRole.GUEST

  return (
    <HeaderPanelUI
      title="Plugin Studio"
      pluginName={activePlugin?.manifest?.name || activePlugin?.name}
      onEditMetadata={activePlugin ? handleEditMetadata : undefined}
      activeView={activeView}
      onTabChange={activePlugin ? handleTabChange : undefined}
    />
  )
}

export default HeaderPanel
